import type { CardPose } from '../card/pose';

/**
 * **手牌摆在哪儿、弯多少 —— 纯数学。**
 *
 * 分两层：
 *
 * ```
 *   fanSlots()    低层：给一组参数，算出每张牌的姿态。不判断"该怎样"
 *        │
 *   handLayout()  高层：按**画布尺寸 + 卡牌尺寸 + 张数**把参数**推导**出来
 * ```
 *
 * ⚠️ 为什么要有高层那一层：手牌的摆法不该是几个手调的数字。
 * 换一屏分辨率、手牌多两张，手调的 `centerX/spread` 立刻就散了。
 * 所以画布尺寸和卡牌尺寸是**输入**，摆法是**输出**。
 *
 * 评审给的规则（原话 → 落到哪个变量）：
 *
 * | 原话 | 落点 |
 * | --- | --- |
 * | 弧度中心 x 总是居中 | `centerX = 画布宽 / 2` |
 * | y 刚好使得中心卡牌贴着底部 | `centerY = 画布高 − 牌高 / 2` |
 * | 间距不大于手牌宽度、不小于 1/5 | `spread ∈ [牌宽/5, 牌宽]` |
 * | 倾角倍率为 1 | `rollGain = 1`（推导出来的，不是旋钮） |
 * | 垂度不小于 0、不大于 3 | `curl ∈ [0, 3]`（单位：**牌高**） |
 * | 垂度使得中心牌完全露出、边缘牌至少露出一半 | 实际垂度再被钳到 `≤ 0.5 牌高` |
 */

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

// ============================================================
// 低层：给参数就摆
// ============================================================

export interface FanParams {
  /** 扇形**正中间那张牌**的中心（逻辑坐标） */
  centerX: number;
  centerY: number;
  /** 相邻两张之间的水平间距（逻辑像素）。负数 = 从左往右排 */
  spread: number;
  /**
   * 弧度：`y += curvature · s²`（`s` = 离正中多少张）。
   *
   * ⚠️ 单位是"每步的平方多少逻辑像素"，所以它**不是**评审说的那个"垂度"。
   * 高层会把"垂度（几个牌高）"换算成它，换算只发生在 `handLayout` 里。
   */
  curvature: number;
  /**
   * 倾斜倍率：1 = 牌**跟着弧线的切线**倒（几何上正确的那种扇形）。
   *
   * 0 = 所有牌都竖直不动，只是位置上排成一条弧 —— 那是"一排牌"，不是扇子。
   */
  rollGain: number;
  /** 基础的 pitch（度）。整手牌共同的前倾 */
  pitch: number;
  /** 每往外一张，pitch 多多少（度）—— 两端的牌"立"起来 */
  pitchPerStep: number;
  /** 相机距离（卡牌长边的倍数）。越小越夸张 */
  perspective: number;
}

export const DEFAULT_FAN: FanParams = {
  centerX: 640,
  centerY: 700,
  spread: 112,
  curvature: 9,
  rollGain: 1,
  pitch: 5,
  pitchPerStep: 2.5,
  perspective: 5,
};

/**
 * 算出手牌每一张的**槽位姿态**。
 *
 * ⚠️ 返回的 `x` / `y` 是**还没有经过交互动画**的目标位置。
 * 想拿最终姿态要走 `feelTarget()`。
 */
export function fanSlots(count: number, p: FanParams): CardPose[] {
  const out: CardPose[] = [];
  for (let i = 0; i < count; i++) {
    // s 是以"正中"为 0 的偏移量。张数是偶数时正中落在两张之间，这是对的
    const s = i - (count - 1) / 2;
    out.push({
      x: p.centerX + s * p.spread,
      y: p.centerY + p.curvature * s * s,
      scale: 1,
      yaw: 0,
      pitch: ((p.pitch + Math.abs(s) * p.pitchPerStep) * Math.PI) / 180,
      // ⚠️ 倾角是**从弧线的切线算出来的**，不是另给一个角度参数 ——
      //    这样"弯度调大"的时候两端的牌会自己跟着倒下去，几何上永远自洽。
      //    `atan2(2cs, spread)` 就是抛物线 y = c·s² 对 s 的斜率
      //
      // ⚠️ 末尾那个 `+ 0` 不是多余的：`atan2(-0, 正数)` 得到 `-0`，
      //    而 `-0` 和 `0` 在 `Object.is` 下**不相等**（快照对比会假红）
      roll: Math.atan2(2 * p.curvature * s, p.spread) * p.rollGain + 0,
      perspective: p.perspective,
    });
  }
  return out;
}

// ============================================================
// 高层：按画布和卡牌尺寸推导
// ============================================================

/**
 * 垂度的量程上界（单位：牌高）。
 *
 * 评审说"垂度不大于 3"，所以参数本身允许到 3；但**实际用到的**会被
 * `MAX_DROOP_ON_SCREEN` 再卡一道 —— 见下。
 */
export const MAX_CURL = 3;

/**
 * 实际能用的垂度上界（单位：牌高）。
 *
 * ⚠️ 这是"边缘牌至少露出一半"直接推出来的：中心牌的底边贴着画布底边，
 * 边缘牌往下坠多少，就有多少露在画布外面。坠超过半个牌高，
 * 露出来的就不足一半了。
 *
 * 所以评审那两条规则里，**后一条是紧的那一条** —— 参数能调到 3，
 * 但一过 0.5 画面就不再变化。调试页会把"想要多少 / 实际多少"两个数都显示出来。
 */
export const MAX_DROOP_ON_SCREEN = 0.5;

/**
 * 边缘牌最多倒多少度。
 *
 * ⚠️ 这条是**必须**的，不是锦上添花：垂度（往下坠多少）和倾角是**同一个抛物线**
 * 的两个侧面 —— `tan(roll) = 2·垂度·牌高 / (sMax · 间距)`。
 * 牌少的时候 `sMax` 很小（两张牌时只有 0.5），为了坠够同样的高度，
 * 抛物线就必须陡得多，于是两张牌会各自歪到 60° 以上 —— 那不是扇子，是散了架。
 *
 * 所以垂度要**同时**被三条上限卡住，取最小的那个：
 * 想要的、边缘牌至少露出一半、以及这条倾角上限。
 */
export const MAX_EDGE_ROLL = 30;

/**
 * 手牌横向最多占画布多宽。留一点边，两端的牌不至于顶到屏幕边。
 *
 * ⚠️ 0.6 而不是 1.0：间距的**上界是牌宽**，所以铺得越开越接近"一张挨一张、
 * 完全不叠"。手牌要的是叠着的扇形，所以默认只用六成宽 ——
 * 7 张时步长约 76px、重叠 31px，看起来才像一只手
 */
export const DEFAULT_FILL = 0.6;

/**
 * 默认想弯多少（单位：牌高）。
 *
 * 上限是 `MAX_DROOP_ON_SCREEN`（0.5），但那正好是"边缘牌只剩一半"。
 * 默认取 0.35：弧线看得出来，边缘牌也不会被切掉太多。
 */
export const DEFAULT_CURL = 0.35;

/**
 * **手牌扇占据的那条横带**（只看 y）。
 *
 * 评审：
 *
 * > 手牌扇的上移改为忽略 x 坐标，仅检测玩家鼠标 y 坐标是否在底部手牌扇占据的
 * > 一整个区域内。
 *
 * 所以"整扇抬起来"这件事的判据是**一条通栏的横带**，不是"指针压在某张牌上"：
 * 在牌与牌之间的缝里、或者扇形左右两边的空处，指针的 y 落在这条带子里，
 * 整扇照样抬起来。而"哪一张被指着"（它自己再抬一点、放大、转向鼠标）还是走
 * 逐张的命中测试 —— 两件事分开。
 *
 * ⚠️ 带子的上沿按**静止姿态**算，再加上"整扇抬起来那一段"的余量：不加上去的
 *    话，指针停在上沿时一抬就把自己顶出带子 → 抬起/落下抖个不停。
 *    （静止姿态是纯数据，和当前动画位置无关，所以不会形成反馈环。）
 */
export function handZone(
  layout: HandLayout,
  card: { readonly height: number },
  lift: number,
): { readonly top: number; readonly bottom: number } {
  let minY = Infinity;
  let maxY = -Infinity;
  for (const slot of layout.slots) {
    if (slot.y < minY) minY = slot.y;
    if (slot.y > maxY) maxY = slot.y;
  }
  if (!Number.isFinite(minY)) {
    // 一张牌都没有：给一条空的（`top > bottom`，谁都不在里面）
    return { top: 0, bottom: -1 };
  }
  return { top: minY - card.height / 2 - lift, bottom: maxY + card.height / 2 };
}

/** 指针的 y 在不在那条横带里。**只看 y** —— x 一律忽略（评审要求） */
export function inHandZone(
  y: number,
  zone: { readonly top: number; readonly bottom: number },
): boolean {
  return y >= zone.top && y <= zone.bottom;
}

/**
 * **拖到出牌线之外的那几张手牌。**
 *
 * ⚠️ **整组一起判**：只要这一组里有牌越过了出牌线，拖出去的就是**整组**。
 *
 * 以前是**逐张**过滤 —— 牌扇里每张牌的目标 y 不一样（弧线），拖到"刚好过线"
 * 时可能只有上面那张算过线。于是松手时这一组被拆成单张打出去、其余退回原位
 * （用户报的"两张一起打出，只生效其中一张，另一张退回"）。
 *
 * targetY 给第 index 张的目标 y；playLineY 是出牌线。
 */
export function outIndices(
  dragSet: readonly number[],
  targetY: (index: number) => number,
  playLineY: number,
): number[] {
  if (!dragSet.some((index) => targetY(index) < playLineY)) return [];
  return [...dragSet];
}

export interface HandLayoutOptions {
  /** 画布（逻辑）尺寸 */
  readonly bounds: { readonly width: number; readonly height: number };
  /** **牌本身**的尺寸（不含出血） */
  readonly card: { readonly width: number; readonly height: number };
  /** 手牌张数 */
  readonly count: number;
  /** 想弯到多少：单位是**牌高**，`[0, 3]`。实际会被 `MAX_DROOP_ON_SCREEN` 卡住 */
  readonly curl?: number;
  /** 手牌横向占画布的比例，`[0.2, 1]` */
  readonly fill?: number;
  readonly pitch?: number;
  readonly pitchPerStep?: number;
  readonly perspective?: number;
}

export interface HandLayout {
  readonly slots: readonly CardPose[];
  readonly centerX: number;
  readonly centerY: number;
  /** 实际用到的间距 */
  readonly spread: number;
  /** **实际**用到的垂度（单位：牌高） */
  readonly droop: number;
  /** 想要但被卡掉的垂度。和 `droop` 不一样就说明撞到某条上限了 */
  readonly wantedDroop: number;
  /** 倾角上限反推出来的垂度上限。读数里显示它，才能看出是哪条在管 */
  readonly rollLimitDroop: number;
  /** 基础的 pitch（弧度）。被拖动的牌要"保持直立"，只留这一份前倾 */
  readonly basePitch: number;
  readonly rollGain: number;
}

/**
 * **按画布和卡牌尺寸把整手牌的摆法推导出来。**
 *
 * 间距的取法：先按"n 张牌铺满画布宽度的 `fill` 倍"算一个理想值，
 * 再钳进评审给的 `[牌宽/5, 牌宽]`。所以
 *
 * - 牌少 → 间距被上界卡住，手牌聚在中间（不会稀稀拉拉铺满屏）
 * - 牌多到铺不下 → 间距被下界卡住（牌会开始互相压，但不会挤成一张）
 */
export function handLayout(o: HandLayoutOptions): HandLayout {
  const w = o.card.width;
  const h = o.card.height;
  const n = Math.max(0, Math.floor(o.count));
  const fill = clamp(o.fill ?? DEFAULT_FILL, 0.2, 1);

  // ① 位置：x 永远居中，y 让中心牌的**底边正好落在画布底边**
  const centerX = o.bounds.width / 2;
  const centerY = o.bounds.height - h / 2;

  // ② 间距：先"铺满"，再钳进 [牌宽/5, 牌宽]
  const gaps = Math.max(1, n - 1);
  const ideal = n <= 1 ? 0 : (o.bounds.width * fill - w) / gaps;
  const spread = n <= 1 ? 0 : clamp(ideal, w / 5, w);

  const sMax = Math.max(0, (n - 1) / 2);

  // ③ 垂度要同时过三关，取最小的那个：
  //    a. 想要多少（评审给的量程 [0, 3]）
  //    b. "边缘牌至少露出一半" → ≤ 0.5 牌高
  //    c. **倾角上限** → `tan(roll) = 2·droop·h / (sMax·spread)`
  //       牌少时 sMax 很小，这一条才是紧的那条（两张牌时能砍到 0.1 牌高）
  const wantedDroop = clamp(o.curl ?? DEFAULT_CURL, 0, MAX_CURL);
  const rollLimitDroop =
    sMax < 1e-9 || spread <= 0
      ? Number.POSITIVE_INFINITY
      : (Math.tan((MAX_EDGE_ROLL * Math.PI) / 180) * sMax * spread) / (2 * h);
  const droop = Math.min(wantedDroop, MAX_DROOP_ON_SCREEN, rollLimitDroop);

  // ④ 把"垂度（几个牌高）"换算成 `curvature`（每步平方多少像素）：
  //    边缘牌在 s = sMax 处，要让 `curvature · s² = droop · 牌高`
  const curvature = sMax < 1e-9 ? 0 : (droop * h) / (sMax * sMax);

  const params: FanParams = {
    centerX,
    centerY,
    spread,
    curvature,
    // 评审：倾角倍率为 1。它不是旋钮，是"跟着弧线切向倒"这条几何约定
    rollGain: 1,
    pitch: o.pitch ?? DEFAULT_FAN.pitch,
    pitchPerStep: o.pitchPerStep ?? DEFAULT_FAN.pitchPerStep,
    perspective: o.perspective ?? DEFAULT_FAN.perspective,
  };

  return {
    slots: fanSlots(n, params),
    centerX,
    centerY,
    spread,
    droop,
    wantedDroop,
    rollLimitDroop,
    basePitch: (params.pitch * Math.PI) / 180,
    rollGain: params.rollGain,
  };
}
